import { Injectable } from '@angular/core';
import { CanActivate } from '@angular/router';
import { EventId } from '@interfaces';
import {
  AccountTypes,
  FilteredAccount,
  FilteredAccountWithMinorSTP,
  FlowTypes,
} from '@interfaces/models';
import { select, Store } from '@ngrx/store';
import { LoggerCodes } from '@shared/modules/logger/logger';
import { AccountRegistrationService } from '@shared/services';
import {
  closeSpinner,
  flowTypeSelection,
  Go,
  LogError,
  LogInfo,
  State,
  TrackProcessError,
  UpdateDisplayedAccounts,
} from '@store/index';
import {
  selectClientAccountsLoaded,
  selectEligibleMinorAccounts,
} from '@store/selectors/client-accounts/client-accounts.selectors';
import { selectMinorEligibility } from '@store/selectors/eligibility/minor/minor-eligibility.selectors';
import { selectClientDateOfBirth } from '@store/selectors/personal-data/personal-data.selectors';
import * as dayjs from 'dayjs';
import { catchError, filter, Observable, of, switchMap, withLatestFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class MinorEligibilityGuard implements CanActivate {
  constructor(
    private globalStore: Store<State>,
    private accountRegistrationService: AccountRegistrationService,
  ) {}

  canActivate(): Observable<boolean> {
    return this.globalStore.pipe(
      select(selectClientAccountsLoaded),
      filter((loaded) => loaded),
      withLatestFrom(
        this.globalStore.select(flowTypeSelection),
        this.globalStore.select(selectMinorEligibility),
        this.globalStore.select(selectEligibleMinorAccounts),
        this.globalStore.select(selectClientDateOfBirth),
      ),
      switchMap(([, flow, minorEligible, minorAccounts, birthDate]) => {
        if (flow !== FlowTypes.MINOR) {
          return of(true);
        }
        if (!minorEligible) {
          this.globalStore.dispatch(
            LogInfo({
              payload: {
                eventId: EventId.INELIGIBLE_PAGE_ROUTE,
                message: `No eligible MINOR UGMA/UTMA TO account`,
                ineligibleFromAccountType: AccountTypes.UGMA_UTMA,
                flowType: FlowTypes.MINOR,
              },
            }),
          );
          this.globalStore.dispatch(Go({ path: ['minor-transfer/ineligible'] }));
          return of(false);
        }
        return this.accountRegistrationService.callAccountRegistration().pipe(
          switchMap((registrationsWithAge) => {
            const age = dayjs().diff(dayjs(birthDate, 'MM/DD/YYYY'), 'years');
            const ageOfTermAccounts = minorAccounts.accounts.reduce(
              (acc: FilteredAccountWithMinorSTP[], account: FilteredAccount) => {
                const matchedAccount = registrationsWithAge.accountRegistrations.find(
                  (agedAccount) => {
                    return account.accountId === agedAccount.accountId;
                  },
                );

                if (matchedAccount && age >= matchedAccount.ageOfTermination) {
                  acc.push({ ...account, verifiedForSTP: matchedAccount.verifiedForSTP });
                } else if (matchedAccount && age === 0) {
                  acc.push({ ...account, verifiedForSTP: false });
                }

                return acc;
              },
              [],
            );

            if (ageOfTermAccounts.length === 0) {
              this.globalStore.dispatch(Go({ path: ['minor-transfer/ineligible'] }));
              this.globalStore.dispatch(closeSpinner());
              this.globalStore.dispatch(
                LogInfo({
                  payload: {
                    eventId: EventId.INELIGIBLE_PAGE_ROUTE,
                    message: `Client (${age}) under the age of termination`,
                    flowType: FlowTypes.MINOR,
                  },
                }),
              );
              this.globalStore.dispatch(
                TrackProcessError({ errorCode: 'coo-minor-ineligible-aot' }),
              );
              return of(false);
            } else {
              this.globalStore.dispatch(UpdateDisplayedAccounts({ payload: ageOfTermAccounts }));
              this.globalStore.dispatch(
                LogInfo({
                  payload: {
                    eventId:
                      'minor-eligibility-guard--eligible-accounts-found-checkMinorAgeOfTermination',
                    message: `Eligible ugmta/utma account`,
                    code: LoggerCodes.GENERAL_SUCCESS,
                  },
                }),
              );
              this.globalStore.dispatch(closeSpinner());
              return of(true);
            }
          }),
        );
      }),
      catchError((err) => {
        this.globalStore.dispatch(Go({ path: ['error'] }));
        this.globalStore.dispatch(closeSpinner());
        this.globalStore.dispatch(
          LogError({
            payload: {
              eventId: `minor-eligibility-guard-error`,
              message: err.message,
            },
          }),
        );
        return of(false);
      }),
    );
  }
}
